// external
import React, { useCallback, useEffect, useState } from 'react';
import { Form, Formik, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { filter } from 'lodash';
import { ButtonPrimary, IconAccount, IconCards, IconCheckBanking, IconInfo } from '@americanexpress/dls-react';

// models and services
import LiteralsService from 'src/services/literals/LiteralsService';
import FormatService from 'src/services/format.service';
import {
    IncomesViewModel,
    ManualIncomesViewModel,
    PlaidIncomesViewModel
} from 'src/types/view-models/onboarding/incomes.view.model';
import { IncomeSourceModel } from 'src/types/income-source.model';
import IncomeSourceService from 'src/services/income-source.service';
import { OTHER_INCOME } from 'src/types/onboarding.enum';
import { DLSSize } from 'src/types/dls-size.enum';
import { BW_PHANT_URL } from 'src/config';

// components
import OnbordingProgress from 'src/components/atomic/molecules/onboardingProgress/OnboardingProgress';
import Tooltip from 'src/components/dlsMods/tooltip/Tooltip';
import Circle from 'src/components/atomic/atoms/circle/Circle';
import CurrencyInput from 'src/components/formik-fields/currency-input/CurrencyInput';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

// styles
import colors from 'src/styles/colors.module.scss';
import bgColors from 'src/styles/bgColors.module.scss';
import styles from './incomes.module.scss';
import Callout from '../../../../atoms/callout/Callout';
import WhyShouldYouConnectTooltip from '../../../../atoms/tooltips/whyYouShouldConnectTooltip/WhyShouldYouConnectTooltip';
import useAsyncCallback from 'src/hooks/useAsyncCallback';

/** Form Values for Onboarding Incomes */
interface OnboardingIncomesFormValues {
    annualIncomePretax: number[];
    otherRecurringPretaxIncome: number;
}

/** Form validation schema */
const validationSchema = Yup.object({
    annualIncomePretax: Yup.array().of(
        Yup.number()
            .test('is-valid', 'Income must be > 0', (value, context) => {
                return (value !== undefined && value !== 0) || context.path !== 'annualIncomePretax[0]';
            })
            .typeError('Must be a number')
    ),
    otherRecurringPretaxIncome: Yup.number().typeError('Must be a number')
});

function ManualIncomes({ persons, onChange }: ManualIncomesViewModel): JSX.Element {
    const { values } = useFormikContext();

    /** When an input changes, call onChange with the current form values */
    useEffect(() => {
        if (validationSchema.isValidSync(values)) void onChange(values);
    }, [values]);

    return (
        <div className={styles.grid}>
            {persons?.map((p, i) => (
                <React.Fragment key={i}>
                    <Circle diameter={'48px'}>
                        {p?.photos[0]?.relativeUrl ? (
                            <img
                                className={styles.profilePhoto}
                                src={`${BW_PHANT_URL}/${p?.photos[0]?.relativeUrl}`}
                                alt={`profile photo for ${p.nickname}`}
                            />
                        ) : (
                            <Circle diameter={'48px'} className={bgColors.backgroundHighlight}>
                                <IconAccount className={styles.icon} size={DLSSize.MEDIUM} />
                            </Circle>
                        )}
                    </Circle>
                    <div className={styles.gridSection}>
                        <Typography variant={TypographyVariants.BODY_BOLD}>
                            {i === 0 ? 'Your ' : `${p.nickname}'s `}
                            {LiteralsService.getLiteral('Onboarding.incomes.annualPretaxIncome')}
                        </Typography>
                        <Typography variant={TypographyVariants.BODY}>
                            {LiteralsService.getLiteral(
                                i === 0 ? 'Onboarding.incomes.basedOn' : 'Onboarding.incomes.anyIncomeTied'
                            )}
                        </Typography>
                        <CurrencyInput
                            id={`annualIncomePretax[${i}]`}
                            data-cy="currency-input"
                            label={
                                (i === 0 ? 'Your ' : `${p.nickname}'s `) +
                                LiteralsService.getLiteral('Onboarding.incomes.annualPretaxIncome')
                            }
                            hideLabel
                        />
                    </div>
                </React.Fragment>
            ))}

            <Circle diameter={'48px'} className={bgColors.backgroundHighlight}>
                <IconCheckBanking className={styles.icon} size="md" />
            </Circle>
            <div className={styles.gridSection}>
                <Typography variant={TypographyVariants.BODY_BOLD}>
                    {LiteralsService.getLiteral('Onboarding.incomes.otherRecurringIncome')}
                </Typography>
                <Typography variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('Onboarding.incomes.incomeNotTiedTo')}
                </Typography>
                <CurrencyInput
                    id="otherRecurringPretaxIncome"
                    data-cy="currency-input"
                    label={LiteralsService.getLiteral('Onboarding.incomes.otherRecurringIncome')}
                    hideLabel
                />
            </div>
        </div>
    );
}

function PlaidIncomes({ incomeSources }: PlaidIncomesViewModel): JSX.Element {
    return (
        <div className={styles.connectedAccountsSection}>
            <div>
                <Typography variant={TypographyVariants.HEADING_XS} className={colors.textStrong}>
                    {LiteralsService.getLiteral('Onboarding.incomes.yourAnnualIncomes')}
                </Typography>
                <Tooltip
                    className={styles.tooltipContainer}
                    triggerElement={<IconInfo className={styles.tooltipIcon} />}
                >
                    <Typography variant={TypographyVariants.HEADING_XS} className={colors.brand}>
                        {LiteralsService.getLiteral('Onboarding.incomes.howWeCalculate')}
                    </Typography>
                    <Typography variant={TypographyVariants.SUBTEXT} component="p" className={colors.brand}>
                        Needs Copy
                    </Typography>
                    <Typography variant={TypographyVariants.SUBTEXT} component="p" className={colors.brand}>
                        {LiteralsService.getLiteral('Onboarding.incomes.learnMoreAbout')}
                    </Typography>
                </Tooltip>
            </div>

            {incomeSources?.map((income, index) => {
                return (
                    <div className={styles.incomeRow} key={`incomeRow-${index}`}>
                        {income.name && (
                            <Typography component="p" variant={TypographyVariants.SUBTEXT_BOLD}>
                                {income.name}
                            </Typography>
                        )}
                        {
                            <Typography component="p" variant={TypographyVariants.BODY}>
                                {FormatService.format(income.annualizedIncome)}
                            </Typography>
                        }
                    </div>
                );
            })}
        </div>
    );
}

/** Incomes View for Onboarding */
export default function Incomes({ incomes, updateIncomes, persons, user, next }: IncomesViewModel): JSX.Element {
    const [incomeSources, setIncomeSources] = useState<IncomeSourceModel[]>();
    const isPlaidConnected = user.accounts?.length > 0;

    /** Initial form values */
    const [initialValues] = useState<OnboardingIncomesFormValues>({
        annualIncomePretax: persons?.map(({ nickname }) => incomes[nickname] ?? 0) || [],
        otherRecurringPretaxIncome: incomes[OTHER_INCOME] ?? 0
    });

    /** When the component switches to <PlaidIncomes>, onChange should be invoked to show the incomes graph */
    useEffect(() => {
        void (async () => {
            if (isPlaidConnected) {
                await onChange(initialValues);
                let newIncomeSources: IncomeSourceModel[] = await IncomeSourceService.getIncomeSources();
                newIncomeSources = filter(newIncomeSources, (incomeSource) => {
                    return incomeSource.annualizedIncome > 0;
                });
                setIncomeSources(newIncomeSources);
            }
        })();
    }, [isPlaidConnected]);

    /** Form change */
    const onChange = useAsyncCallback(async (values: OnboardingIncomesFormValues): Promise<void> => {
        if (values.annualIncomePretax?.length !== persons.length)
            console.warn(
                `The number of inputs values received does not match the number of persons. values:${values.annualIncomePretax?.length}, persons:${persons.length}`
            );

        // Only add other recurring pretax income if not 0 and undefined
        let newIncomes: { [key: string]: any } = {};
        if (values.otherRecurringPretaxIncome && values.otherRecurringPretaxIncome > 0) {
            newIncomes = { [OTHER_INCOME]: values.otherRecurringPretaxIncome };
        }

        for (let i = 0; i < values.annualIncomePretax?.length; i++)
            newIncomes[persons[i].nickname] = values.annualIncomePretax[i];

        await updateIncomes(newIncomes, isPlaidConnected); // TODO this should be a service call, not props
    }, []);

    /** Form submit */
    const onSubmit = useCallback(async (values: OnboardingIncomesFormValues): Promise<void> => {
        await onChange(values);
        await next();
    }, []);

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={isPlaidConnected ? undefined : validationSchema}
            onSubmit={onSubmit}
        >
            <Form className={styles.incomes}>
                <OnbordingProgress index={1} length={7} />
                <Typography variant={TypographyVariants.BODY}>
                    {LiteralsService.getLiteral('Onboarding.incomes.enterTheAnnual')}
                </Typography>

                {isPlaidConnected ? (
                    <PlaidIncomes incomeSources={incomeSources} />
                ) : (
                    <ManualIncomes persons={persons} onChange={onChange} />
                )}

                <Callout
                    icon={<IconCards size={DLSSize.MEDIUM} />}
                    heading={LiteralsService.getLiteral('Onboarding.incomes.wantUptodateInfo')}
                >
                    <Typography variant={TypographyVariants.BODY}>
                        {LiteralsService.getLiteral('Onboarding.incomes.connectYourAccounts')}
                    </Typography>
                    <WhyShouldYouConnectTooltip withText />
                </Callout>

                <div>
                    <ButtonPrimary type="submit">{LiteralsService.getLiteral('Onboarding.next')}</ButtonPrimary>
                </div>
            </Form>
        </Formik>
    );
}
